import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DailyData } from '../../../common/model/dto/daily-data';
import { UserCycle } from '../../../common/model/dto/user-cycle/user-cycle';
import {
  ChartRangeMode,
  CustomChartRangeSelection,
} from '../../../common/model/type/chart-range-selection.type';
import { DateRange } from '../../../common/model/type/date-range.type';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import CycleSelector from './CycleSelector';
import OvUserChartsDaterangeSelector from './OvUserChartsDaterangeSelector';
import RangeModeToggle from './RangeModeToggle';

interface OvUserChartsRangeSelectorProps {
  mode: ChartRangeMode;
  onModeChange: (mode: ChartRangeMode) => void;
  cycles: UserCycle[];
  cyclesLoading: boolean;
  selectedCycleId: string;
  onCycleSelect: (cycle: UserCycle) => void;
  customSelection: CustomChartRangeSelection;
  customDateRange: DateRange;
  onCustomSelectionChange: (selection: CustomChartRangeSelection) => void;
  customRangeText: string;
  dailyDataInCalendar: DailyData[];
  onCalendarRangeChange: (startDate: Date, endDate: Date) => void;
}

const OvUserChartsRangeSelector: FC<OvUserChartsRangeSelectorProps> = ({
  mode,
  onModeChange,
  cycles,
  cyclesLoading,
  selectedCycleId,
  onCycleSelect,
  customSelection,
  customDateRange,
  onCustomSelectionChange,
  customRangeText,
  dailyDataInCalendar,
  onCalendarRangeChange,
}) => {
  const { t } = useTranslation();

  return (
    <Header>
      <RangeModeToggle
        mode={mode}
        onChange={onModeChange}
        customLabel={t('dashboard.rangeMode.custom')}
        cycleLabel={t('dashboard.rangeMode.cycle')}
        ariaLabel={t('dashboard.rangeMode.ariaLabel')}
        customAriaLabel={t('dashboard.rangeMode.customAriaLabel')}
        cycleAriaLabel={t('dashboard.rangeMode.cycleAriaLabel')}
        cycleDisabled={!cyclesLoading && cycles.length === 0}
      />

      {mode === 'cycle' ? (
        <CycleSelector
          cycles={cycles}
          selectedCycleId={selectedCycleId}
          onCycleSelect={onCycleSelect}
          isLoading={cyclesLoading}
        />
      ) : (
        <OvUserChartsDaterangeSelector
          selection={customSelection}
          defaultText={customRangeText}
          dateRange={customDateRange}
          onSelectionChange={onCustomSelectionChange}
          dailyDataInCalendar={dailyDataInCalendar}
          onCalendarRangeChange={onCalendarRangeChange}
          embedded
        />
      )}
    </Header>
  );
};

export default OvUserChartsRangeSelector;

const Header = styled.header`
  display: flex;
  align-items: center;
  border-radius: 0.5rem;
  background-color: ${Colours.OV_BASE};
  color: ${Colours.OV_SEMI_LIGHT};
  margin-bottom: 1.5rem;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    padding-bottom: 0.5rem;
  }
`;
